import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './style/Test.css';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import TableBlock from '../components/TableBlock';
import Clock from '../images/clock.png';
import notFlagged from '../images/notFlagged.png';
import flagged from '../images/flagged.png';
import ToolsPanel from '../components/ToolsPanel';
import { authFetch } from '../authFetch';

function Test() {
  const [pageLoading, setPageLoading] = useState(true);
  const [questionLoading, setQuestionLoading] = useState(true);
  const [answerClass, setAnswerClass] = useState(['', '', '', '', '']);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [questionData, setQuestionData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [timeRemaining, setTimeRemaining] = useState(35 * 60);
  const location = useLocation();
  const navigate = useNavigate();
  const testId = location.state?.testId;
  const attemptNumber = location.state?.attemptNumber || 1;
  const [answeredQuestions, setAnsweredQuestions] = useState(Array.from({ length: 22 }, () => false));
  const [flaggedQuestions, setFlaggedQuestions] = useState(Array.from({ length: 22 }, () => false));
  const [toolsCollapsed, setToolsCollapsed] = useState(false);
  const [desmosReady, setDesmosReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const testName = location.state?.testName || "";
  const timed = location.state?.timed || false;

  const initialLoad = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_BASE}/test/initialLoad`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          testId,
          attemptNumber,
          timed
        }),
      });
      const data = await response.json();
      for (let i = 0; i < data.attemptedQuestions.length; i++) {
        if (data.attemptedQuestions[i].flagged) {
          setFlaggedQuestions(prev => {
            const updated = [...prev];
            updated[data.attemptedQuestions[i].question_number - 1] = true;
            return updated;
          });
        }
        if (data.attemptedQuestions[i].selected_answer !== null) {
          setAnsweredQuestions(prev => {
            const updated = [...prev];
            updated[data.attemptedQuestions[i].question_number - 1] = true;
            return updated;
          });
        }
      };
    }
    catch (error) {
      console.error('Error:', error);
    }
  };

  const loadQuestion = async (questionNum) => {
    setQuestionLoading(true);
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_BASE}/test/loadQuestion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          testId,
          attemptNumber,
          questionNum,
        }),
      });
      const data = await response.json();
      if (data.questionData) {
        setQuestionData(data.questionData);
        if (data.selectedAnswer !== null) {
          let answer = null;
          if (data.questionData.question_type === 'FRQ') { answer = data.selectedAnswer; }
          else { answer = parseInt(data.selectedAnswer); }
          setSelectedAnswer(answer);
          setAnswerClass(prev => prev.map((_, i) => i === answer ? 'selected' : ''));
        }
        else {
          setAnswerClass(['', '', '', '', '']);
          setSelectedAnswer(null);
        }
        setQuestionLoading(false);
      }
      else { console.log('error'); }
    }
    catch (error) {
      console.error('Error:', error);
    }
  };

  const updateAnswer = async (index) => {
    try {
      await authFetch(`${process.env.REACT_APP_API_BASE}/test/updateAnswer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          testId,
          questionNumber: currentQuestion,
          selectedAnswer: index,
          attemptNumber
        }),
      });
    }
    catch (error) {
      console.error('Error:', error);
    }
  };

  const updateFlag = async (flagged) => {
    try {
      await authFetch(`${process.env.REACT_APP_API_BASE}/test/updateFlag`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          testId,
          questionNumber: currentQuestion,
          flagged,
          attemptNumber
        }),
      });
    }
    catch (error) {
      console.error('Error:', error);
    }
  };

  const submitTest = async () => {
    setSubmitting(true);
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_BASE}/test/submitTest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          testId,
          attemptNumber,
        }),
      });
      const data = await response.json();
      if (data.success) {
        navigate(`/tests/${testName}/review`, {
          state: { attemptNumber, testName, testId }
        });
      }
    }
    catch (error) {
      console.error('Error:', error);
    }
    finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };



  const handleFlagClick = (questionNum) => () => {
    const flagged = !flaggedQuestions[questionNum - 1];
    setFlaggedQuestions(prev => {
      const updated = [...prev];
      updated[questionNum - 1] = flagged;
      return updated;
    });
    updateFlag(flagged);
  }

  const handleAnswerClick = (index) => {
    if (index === selectedAnswer) {
      setSelectedAnswer(null);
      setAnswerClass(['', '', '', '', '']);
      setAnsweredQuestions(prev => {
        const updated = [...prev];
        updated[currentQuestion - 1] = false;
        return updated;
      });
      updateAnswer(null);
    }
    else {
      setSelectedAnswer(index);
      setAnswerClass(prev => prev.map((_, i) => i === index ? 'selected' : ''));
      setAnsweredQuestions(prev => {
        const updated = [...prev];
        updated[currentQuestion - 1] = true;
        return updated;
      });
      updateAnswer(index);
    }
  };

  const handleQuestionButtonClick = (questionNum) => {
    setCurrentQuestion(questionNum);
    loadQuestion(questionNum);
  };

  const handleNextClick = () => {
    if (currentQuestion < 22) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      loadQuestion(nextQuestion);
    }
  };

  const handlePreviousClick = () => {
    if (currentQuestion > 1) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
      loadQuestion(prevQuestion);
    }
  };

  useEffect(() => {
    if (!location.state) {
      navigate('/tests', { replace: true });
      return;
    }
    initialLoad();
    loadQuestion(currentQuestion);
  }, []);

  useEffect(() => {
    const isReady = questionData && desmosReady;
    setPageLoading(!isReady);
  }, [questionData, desmosReady]);

  useEffect(() => {
    if (!timed || timeRemaining <= 0) return;
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timed, timeRemaining]);

  return (
    <div className='test-container'>
      <div className='mode-header'>
        <p>#{testName}</p>
      </div>
      <div className={`main-layout ${toolsCollapsed ? 'tools-collapsed' : ''}`} style={pageLoading ? { visibility: 'hidden', position: 'absolute' } : undefined}>
        <ToolsPanel onReady={() => setDesmosReady(true)} resetKey={currentQuestion} onCollapse={setToolsCollapsed} />
        <div className='question-content'>
          {!questionLoading && <div className='question-box'>
            <div className='question-info'>
              <p>{`${currentQuestion}`}</p>
              {flaggedQuestions[currentQuestion - 1] ? (
                <img src={flagged} alt="Flagged" onClick={handleFlagClick(currentQuestion)} />
              ) : (
                <img src={notFlagged} alt="Not Flagged" onClick={handleFlagClick(currentQuestion)} />
              )}
            </div>
            <div className='question-body'>
              <div className='question-text'>
                <p>
                  {questionData ? (
                    questionData.content.map((block, i) => {
                      switch (block.type) {
                        case "text":
                          return <span key={i}>{block.content} </span>;
                        case "inline_math":
                          return <InlineMath key={i} math={block.content} />;
                        case "block_math":
                          return <BlockMath key={i} math={block.content} />;
                        case "image":
                          return <img key={i} src={`${process.env.REACT_APP_API_BASE}${block.src}`} alt={block.alt} />;
                        case "table":
                          return <TableBlock key={i} headers={block.headers} rows={block.rows} />;
                        case "line_break":
                          return <br key={i} />;
                        default:
                          return null;
                      }
                    })
                  ) : null}
                </p>
              </div>
              <div className='answer-options'>
                {questionData && questionData.question_type === 'FRQ' ? (
                  <input
                    type="text"
                    value={selectedAnswer === null ? '' : selectedAnswer}
                    onChange={(e) => {
                      setSelectedAnswer(e.target.value.length === 0 ? null : e.target.value);
                      updateAnswer(e.target.value.length === 0 ? null : e.target.value);
                      if (e.target.value.length === 0) {
                        setAnsweredQuestions(prev => {
                          const updated = [...prev];
                          updated[currentQuestion - 1] = false;
                          return updated;
                        });
                      }
                      else {
                        setAnsweredQuestions(prev => {
                          const updated = [...prev];
                          updated[currentQuestion - 1] = true;
                          return updated;
                        });
                      }
                    }}

                    className={answerClass[4]}
                  />
                ) : (
                  <>
                    {['A', 'B', 'C', 'D'].map((label, index) => (
                      <button key={index} className={answerClass[index]} onClick={() => handleAnswerClick(index)}>
                        <p>{`${label})`}</p>
                        <p>
                          {questionData && questionData.answer_choices && questionData.answer_choices[index] ? (
                            questionData.answer_choices[index].map((block, i) => {
                              switch (block.type) {
                                case "text":
                                  return <span key={i}>{block.content}</span>;
                                case "inline_math":
                                  return <InlineMath key={i} math={block.content} />;
                                case "block_math":
                                  return <BlockMath key={i} math={block.content} />;
                                case "image":
                                  return <img key={i} src={`${process.env.REACT_APP_API_BASE}${block.src}`} alt={block.alt} />;
                                case "table":
                                  return <TableBlock key={i} headers={block.headers} rows={block.rows} />;
                                default:
                                  return null;
                              }
                            })
                          ) : ''}
                        </p>
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
          }
          {questionLoading && <div className='question-box-loading'>

          </div>}
          <div className='question-numbers'>
            {Array.from({ length: 22 }, (_, i) => i + 1).map((num) => {
              const classes = [];
              if (num === currentQuestion) classes.push('active');
              if (flaggedQuestions[num - 1]) classes.push('flagged');
              else if (answeredQuestions[num - 1]) classes.push('answered');
              return (
                <button
                  key={num}
                  className={classes.join(' ')}
                  onClick={() => handleQuestionButtonClick(num)}
                >
                  {num}
                </button>
              );
            })}
          </div>
          <div className='action-controls'>
            {timed && <div className='timer'>
              <img src={Clock} alt="Clock" />
              <p>{formatTime(timeRemaining)}</p>
            </div>}
            <div className='navigation-buttons'>
              <button id='previous' onClick={handlePreviousClick}>Previous</button>
              {currentQuestion === 22 ? (
                <button onClick={submitTest}>Submit</button>
              ) : (
                <button id='next' onClick={handleNextClick}>Next</button>
              )}
            </div>
          </div>
        </div>
      </div>
      {pageLoading && <div className='main-layout-loading'>
        <div className='three-circle-loader'>
          <span />
          <span />
          <span />
        </div>
      </div>}
      {submitting && (
        <div className='submit-overlay'>
          <div className='submit-loader'>
            <div className='three-circle-loader'>
              <span />
              <span />
              <span />
            </div>
            <p>Grading your test...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Test;